import React, { Component } from 'react';
import { FaIcon } from '../../FaIcon';
import { withTranslation } from 'react-i18next';
import { PLANS, PERIODS, getPrice, formatPrice } from '../../upgrade_to_dashbo/pricingConfig';

const CUSTOMIZABLE_FEATURES = {
    budget_partial_goals_enabled: { labelKey: 'agency_subscription.partial_budget_goals_module', isBoolean: true },
    module_multi_currency_enabled: { labelKey: 'agency_subscription.multi_currency_module', isBoolean: true },
    module_access_configuration_enabled: { labelKey: 'agency_subscription.admin_users_module', isBoolean: true },
    notifications_to_webhook_enabled: { labelKey: 'agency_subscription.webhook', isBoolean: true },
    module_flex_indicators_enabled: { labelKey: 'agency_subscription.custom_columns', isBoolean: true },
    ad_group_level_enabled: { labelKey: 'agency_subscription.ad_group_level_module', isBoolean: true },
    module_node_to_sheets_enabled: { labelKey: 'agency_subscription.google_sheets_sync_module', isBoolean: true },
    looker_cc_enabled: { labelKey: 'agency_subscription.looker_studio_connector', isBoolean: true },
    data_node_enabled: { labelKey: 'agency_subscription.data_node', isBoolean: true },
    module_dashbo_mcp_enabled: { labelKey: 'agency_subscription.dashbo_connect', isBoolean: true },
    max_ad_account_linkable: { labelKey: 'agency_subscription.max_ad_accounts_linkable', isBoolean: false },
    max_urls_per_ad_account: { labelKey: 'agency_subscription.urls_to_check_per_ad_account', isBoolean: false },
    module_node_to_sheets_max_sync_configuration: { labelKey: 'agency_subscription.max_sync_configurations', isBoolean: false },
};

const TIER_ORDER = { profesional: 0, agency: 1, big_agency: 2, epic_agency: 3 };
const TIER_LABELS = {
    profesional: 'Profesional',
    agency: 'Agency',
    big_agency: 'Big Agency',
    epic_agency: 'Epic Agency',
    custom: 'Custom'
};
const PERIOD_OPTIONS = [
    { id: 'yearly', labelKey: 'agency_subscription.period_yearly' },
    { id: 'semestral', labelKey: 'agency_subscription.period_semestral' },
    { id: 'trimestral', labelKey: 'agency_subscription.period_trimestral' },
    { id: 'monthly', labelKey: 'agency_subscription.period_monthly' },
];

// Las variantes `<plan>_with_looker` existían mientras Looker Studio se vendía aparte. Desde el
// pricing del 2026-10-05 Looker va incluido en todos los planes: la variante es el mismo plan.
const baseTierOf = (tier) => (tier ? tier.replace(/_with_looker$/, '') : tier);

class PlanChangeSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currency: 'usd',
            period: 'monthly',
        };
    }

    // Esta grilla vive sólo en la prueba y en la suscripción vencida: elegir un plan es el checkout. La
    // agencia paga cambia de plan desde `paid/PaidSubscriptionView`, que pide el cambio por el chat; el
    // pedido al webhook de Make (`agency/subscription/change-plan/`) quedó sin uso (tanda «Suscripción y
    // pagos», D-2) y se borró de acá.
    selectPlan(plan) {
        const { isFreeTrial, isExpired, onStartCheckout } = this.props;
        const { period } = this.state;

        if ((isFreeTrial || isExpired) && onStartCheckout) {
            onStartCheckout({ plan, period });
        }
    }

    hasOverrides() {
        const { subscriptionData } = this.props;
        if (!subscriptionData) return false;
        const planBase = subscriptionData.plan_base_features;
        if (!planBase) return false;
        return Object.keys(planBase).some(key => {
            if (key === 'max_ad_account_linkable') return subscriptionData.max_ad_account_linkable !== planBase[key];
            if (key === 'max_urls_per_ad_account') return subscriptionData.max_urls_per_ad_account !== planBase[key];
            return subscriptionData[key] !== planBase[key];
        });
    }

    getOverridesList() {
        const { subscriptionData } = this.props;
        if (!subscriptionData) return [];
        const planBase = subscriptionData.plan_base_features;
        if (!planBase) return [];

        const overrides = [];
        for (const [key, config] of Object.entries(CUSTOMIZABLE_FEATURES)) {
            const baseValue = planBase[key];
            if (baseValue === undefined || baseValue === null) continue;
            const currentValue = subscriptionData[key];
            if (currentValue !== baseValue) {
                overrides.push({
                    key,
                    labelKey: config.labelKey,
                    isBoolean: config.isBoolean,
                    baseValue,
                    currentValue,
                });
            }
        }
        return overrides;
    }

    renderPlanCTA(plan) {
        const { t, isFreeTrial, isExpired } = this.props;
        const currentTier = baseTierOf(this.props.currentTier);
        const isCurrent = currentTier === plan.id;
        const currentOrder = TIER_ORDER[currentTier];
        const planOrder = TIER_ORDER[plan.id];
        const isCustomOrNull = !currentTier || currentTier === 'custom';

        // Free trial y expirados: siempre mostrar "Elegir plan"
        if (isFreeTrial || isExpired) {
            return (
                <button className="btn -alt pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                    {t('agency_subscription.choose_plan')}
                    <FaIcon iconName="arrow-right" />
                </button>
            );
        }

        if (isCurrent) {
            return (
                <span className="pricing-card-cta -current">
                    {t('agency_subscription.current_plan')}
                </span>
            );
        }

        if (isCustomOrNull) {
            return (
                <button className="btn -alt -negative pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                    {t('agency_subscription.request_change')}
                </button>
            );
        }

        if (planOrder > currentOrder) {
            return (
                <button className="btn -alt pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                    {t('agency_subscription.upgrade')}
                    <FaIcon iconName="arrow-up" />
                </button>
            );
        }

        return (
            <button className="btn -alt -negative pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                {t('agency_subscription.downgrade')}
            </button>
        );
    }

    // La grilla del pricing del 2026-10-05: los planes se distinguen por cantidades; las funciones
    // (Looker Studio incluido) son las mismas en los cuatro.
    renderPlanGrid(plan) {
        const { t } = this.props;
        const { quantities, premiumSupport } = plan;

        return (
            <>
                <ul className="pricing-card-features -quantities">
                    <li className="feature-included">
                        <FaIcon iconName="check" className="feature-icon -yes" />
                        <span>
                            {t('subscription_features.connected_accounts', { accounts: quantities.linkedAccounts })}
                            <span className="pricing-card-feature-hint">
                                {t('subscription_features.approx_clients', { clients: quantities.approxClients })}
                            </span>
                        </span>
                    </li>
                    <li className="feature-included">
                        <FaIcon iconName="check" className="feature-icon -yes" />
                        <span>
                            {quantities.teamUsers === null
                                ? t('subscription_features.team_users_unlimited')
                                : t('subscription_features.team_users', { users: quantities.teamUsers })}
                        </span>
                    </li>
                    <li className="feature-included">
                        <FaIcon iconName="check" className="feature-icon -yes" />
                        <span>{t('subscription_features.portal_accesses', { accesses: quantities.portalAccesses })}</span>
                    </li>
                </ul>
                <div className="pricing-connect-callout">
                    <div className="connect-callout-header">
                        <FaIcon iconName="check" className="feature-icon -yes" />
                        <span className="connect-callout-title">{t('subscription_features.all_features')}</span>
                    </div>
                    <p className="connect-callout-subtitle">{t('subscription_features.all_features_list')}</p>
                </div>
                <ul className="pricing-card-features -services">
                    <li className="feature-included">
                        <FaIcon iconName="check" className="feature-icon -yes" />
                        <span>{t('subscription_features.services_all')}</span>
                    </li>
                    <li className={premiumSupport ? 'feature-included' : 'feature-excluded'}>
                        <FaIcon iconName={premiumSupport ? 'check' : 'xmark'} className={`feature-icon ${premiumSupport ? '-yes' : '-no'}`} />
                        <span>{t('subscription_features.services_premium')}</span>
                    </li>
                </ul>
            </>
        );
    }

    renderPlanHeader() {
        const { t, subscriptionData, isFreeTrial, isExpired } = this.props;
        if (!subscriptionData) return null;

        const planTier = baseTierOf(subscriptionData.plan_tier);
        const tierLabel = planTier ? (TIER_LABELS[planTier] || planTier) : null;
        const validTill = subscriptionData.subscription_valid_till;
        const hasOverrides = this.hasOverrides();
        const isActive = !isFreeTrial && !isExpired && validTill;

        return (
            <div className="subscription-plan-header">
                <div className="plan-header-left">
                    <div className="plan-header-name-row">
                        <h2 className="plan-header-name">{tierLabel || t('agency_subscription.plan')}</h2>
                        {isFreeTrial && (
                            <span className="plan-header-badge -trial">{t('agency_subscription.free_trial')}</span>
                        )}
                        {isActive && (
                            <span className="plan-header-badge -active">{t('agency_subscription.subscription_active')}</span>
                        )}
                        {isExpired && !isFreeTrial && (
                            <span className="plan-header-badge -expired">{t('agency_subscription.subscription_inactive')}</span>
                        )}
                        {hasOverrides && !isFreeTrial && (
                            <span className="plan-header-badge -custom">{t('agency_subscription.custom_features_badge')}</span>
                        )}
                    </div>
                    {isFreeTrial && validTill && (
                        <div className="plan-header-trial-expiry">
                            {t('agency_subscription.trial_ends_on')} <strong>{validTill}</strong>
                        </div>
                    )}
                    {!isFreeTrial && validTill && (
                        <div className="plan-header-validity">
                            {t('agency_subscription.valid_until_date')} {validTill}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { t, currentTier } = this.props;
        const { period } = this.state;
        const hasOverrides = this.hasOverrides();
        const overridesList = hasOverrides ? this.getOverridesList() : [];
        const showCustomInfo = hasOverrides;

        return (
            <div className="upgrade-to-dashbo-page subscription-plan-chooser">
                <div className="upgrade-pricing">
                    {this.renderPlanHeader()}

                    {showCustomInfo && (
                        <div className="subscription-custom-notice">
                            <FaIcon iconName="circle-info" />
                            <div>
                                <span>{t('agency_subscription.custom_plan_info')}</span>
                                {overridesList.length > 0 && (
                                    <ul className="custom-notice-overrides-list">
                                        {overridesList.map(o => (
                                            <li key={o.key}>
                                                {t(o.labelKey)}
                                                {o.isBoolean
                                                    ? <span className={`override-badge ${o.currentValue ? '-enabled' : '-disabled'}`}>
                                                        {o.currentValue ? t('agency_subscription.included') : t('agency_subscription.not_included')}
                                                      </span>
                                                    : <span className="override-badge -value">
                                                        {t('agency_subscription.override_value_template', { value: o.currentValue, base: o.baseValue })}
                                                      </span>
                                                }
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="subscription-plans-section">
                        <h3 className="upgrade-section-title">{t('agency_subscription.plans_title')}</h3>
                        <p className="upgrade-section-subtitle">{t('agency_subscription.plans_subtitle')}</p>
                    </div>

                    {currentTier === 'custom' && (
                        <div className="subscription-custom-notice">
                            <FaIcon iconName="circle-info" />
                            <span>{t('agency_subscription.custom_plan_message')}</span>
                        </div>
                    )}

                    <div className="pricing-toggles">
                        {/* Currency toggle */}
                        <div className="toggle-group currency-toggle">
                            <button
                                className={`toggle-btn ${this.state.currency === 'usd' ? '-active' : ''}`}
                                onClick={() => this.setState({ currency: 'usd' })}
                            >USD</button>
                            <button
                                className={`toggle-btn ${this.state.currency === 'ars' ? '-active' : ''}`}
                                onClick={() => this.setState({ currency: 'ars' })}
                            >ARS</button>
                        </div>

                        {/* Period toggle */}
                        <div className="toggle-group period-toggle">
                            {PERIOD_OPTIONS.map((opt) => {
                                const discount = PERIODS[opt.id].discount;
                                return (
                                    <button
                                        key={opt.id}
                                        className={`toggle-btn ${period === opt.id ? '-active' : ''}`}
                                        onClick={() => this.setState({ period: opt.id })}
                                    >
                                        {t(opt.labelKey)}
                                        {discount > 0 && <span className="discount-tag">-{discount}%</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {this.state.currency === 'ars' && (
                        <p className="pricing-ars-note">{t('agency_subscription.ars_note')}</p>
                    )}

                    <div className="pricing-cards-grid">
                        {PLANS.map((plan) => {
                            const currency = this.state.currency;
                            const totalPrice = getPrice(plan, currency, period);
                            const isCurrent = baseTierOf(currentTier) === plan.id;
                            const currencyPrefix = currency === 'ars' ? '$' : 'US$';
                            const formattedTotal = currency === 'ars' ? formatPrice(totalPrice, currency) : totalPrice;

                            return (
                                <div key={plan.id} className={`pricing-card ${plan.highlighted ? '-highlighted' : ''} ${isCurrent ? '-current' : ''}`}>
                                    {plan.highlighted && !isCurrent && (
                                        <div className="pricing-card-badge">{t('agency_subscription.recommended')}</div>
                                    )}
                                    {isCurrent && (
                                        <div className="pricing-card-badge -current-badge">{t('agency_subscription.current_plan')}</div>
                                    )}
                                    <div className="pricing-card-header">
                                        <h3 className="pricing-card-name">
                                            {t(plan.nameKey)}
                                            {isCurrent && showCustomInfo && (
                                                <span className="pricing-card-custom-tag">{t('agency_subscription.custom_override')}</span>
                                            )}
                                        </h3>
                                        <p className="pricing-card-description">{t(plan.descriptionKey)}</p>
                                    </div>
                                    <div className="pricing-card-price">
                                        <span className="price-amount">{currencyPrefix}{formattedTotal}</span>
                                        <span className="price-period">/{t('agency_subscription.month_short')}</span>
                                        {currency === 'ars' && <span className="price-iva"> + IVA</span>}
                                    </div>
                                    {this.renderPlanGrid(plan)}
                                    {isCurrent && showCustomInfo && overridesList.length > 0 && (
                                        <div className="pricing-card-overrides">
                                            <div className="pricing-card-overrides-title">
                                                <FaIcon iconName="diamond" />
                                                {t('agency_subscription.plan_customizations_title')}
                                            </div>
                                            <ul className="pricing-card-overrides-list">
                                                {overridesList.map(o => (
                                                    <li key={o.key}>
                                                        <FaIcon iconName="star" className="override-icon" />
                                                        <span className="override-label">{t(o.labelKey)}</span>
                                                        {o.isBoolean
                                                            ? <span className={`override-badge ${o.currentValue ? '-enabled' : '-disabled'}`}>
                                                                {o.currentValue ? t('agency_subscription.included') : t('agency_subscription.not_included')}
                                                              </span>
                                                            : <span className="override-badge -value">
                                                                {t('agency_subscription.override_value_template', { value: o.currentValue, base: o.baseValue })}
                                                              </span>
                                                        }
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {this.renderPlanCTA(plan)}
                                </div>
                            );
                        })}
                    </div>

                    <div className="subscription-pricing-footer">
                        <span>{t('agency_subscription.enterprise_contact')}</span>
                        <button
                            className="btn -alt -negative subscription-pricing-footer-cta"
                            onClick={() => this.selectPlan({ id: 'enterprise', nameKey: 'upgrade_to_dashbo.plans.enterprise.name' })}
                        >
                            <FaIcon iconName="comments" />
                            {t('agency_subscription.contact_us')}
                        </button>
                    </div>

                    <p className="subscription-pricing-note">
                        {t(this.state.currency === 'ars' ? 'agency_subscription.prices_monthly_reference_ars' : 'agency_subscription.prices_monthly_reference')}
                    </p>
                </div>

            </div>
        );
    }
}

export default withTranslation()(PlanChangeSection);
